import {
  getErrorMsg,
  onErrorNotice,
  defaultError,
  httpStatusTitle,
  GlobalResponseError,
  isValue,
  camelCase,
} from '@bluevision/tools';
import type { Request } from '@bluevision/tools';
import { extend } from 'umi-request';
import { stringify } from 'qs';
import type { ResponseError } from 'umi-request/types';
import { history } from 'umi';
import * as Sentry from '@sentry/browser';
import { convertUrl, symbolEnum } from '../convertUrl';
import { isPlainObject } from 'lodash';

import { lowerCase } from './utils';
import { trackEvent } from '@/posthog';

export class CustomError extends Error {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  constructor(message: any, name: string) {
    super(message);
    this.name = name;
  }
}

const errorHandler = (error: ResponseError): void => {
  const { data, response } = error;
  if (response) {
    const { status, statusText } = response;
    const title = httpStatusTitle[status] ?? statusText ?? '请求错误';
    const errorData = data && typeof data === 'object' ? camelCase(data) : title;
    if (status === 401) {
      // 重新登录后跳转到当前 URL
      if (window.location.pathname !== '/user/login') {
        history.replace({
          pathname: '/user/login',
          search: stringify({ redirect: window.location.href }),
        });
      }
      // 401 仅跳转 不弹框
      // throw new GlobalResponseError(errorData, title)
    } else if (status === 400 || status === 404) {
      throw new GlobalResponseError(errorData, data?.msg ?? title);
    } else if (response.status >= 500) {
      throw new GlobalResponseError(errorData, title);
    } else {
      throw new GlobalResponseError(errorData, title);
    }
  } else {
    throw new GlobalResponseError(null, 'unknown error');
  }
};

const umiRequest = extend({
  errorHandler, // 默认错误处理
  credentials: 'include', // 默认请求是否带上cookie
  timeout: 1000 * 60 * 10,
  paramsSerializer: (params) => stringify(params, { arrayFormat: 'comma' }),
});

umiRequest.interceptors.request.use((url, { data, originData, params, ...restOpts }) => ({
  url,
  options: {
    ...restOpts,
    data:
      originData ??
      lowerCase(
        isPlainObject(data) ? convertUrl(data, symbolEnum.SPECIAL, symbolEnum.COMMA) : data,
      ),
    params: lowerCase(params, restOpts.excludeTransformKeys),
  },
}));

const request: Request = async (url, options) => {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const { notice, isCamel = true, getResponse }: any = options;
  try {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const response: any = await umiRequest(url, options);

    trackEvent('api', {
      url,
      requestParams: options?.params,
      requestData: options?.data,
      ...Object.fromEntries(
        Object.entries(options ?? {}).filter(([k]) => !['params', 'data', 'notice'].includes(k)),
      ),

      responseStatus: response?.status,
      responseMsg: response?.msg,
      responseData: JSON.stringify(
        Object.fromEntries(
          Object.entries(response ?? {}).filter(([k]) => !['status', 'msg', 'code'].includes(k)),
        ),
      ),
    });

    if (response && typeof response === 'object') {
      if (getResponse) {
        return [response, null];
      }
      const { status, data, msg, failed_list, success } = response;

      if (status === 1 || !!success) {
        return [isCamel ? camelCase(data) : data, null];
      }
      if (status === 2) {
        return [null, isCamel ? camelCase(failed_list) : failed_list];
      }
      const errorMsg = msg ?? defaultError;
      const errorRes = isValue(data) ? (isCamel ? camelCase(data) : data) : errorMsg;
      if (notice) {
        onErrorNotice(notice, errorMsg, undefined, defaultError);
      }
      return [null, errorRes];
    }
    return [response, null];
  } catch (error) {
    Sentry.captureException(new CustomError(error, 'RequestError'));
    if (notice) {
      onErrorNotice(notice || url.split('/api/')[1], error, undefined, defaultError);
    }
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    return [null, getErrorMsg(error as any, defaultError)];
  }
};

export default request;
